import React, { useState, useEffect } from 'react';
import { X, User, Clock, CheckCircle, AlertCircle, Play, FileText } from 'lucide-react';
import { api } from '../auth/authStore';
import toast from 'react-hot-toast';

interface TaskStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  task: Task | null;
}

interface Task {
  id: string;
  title: string;
  status: string;
  assignees: {
    preparerId?: string;
    reviewerId?: string;
    submitterId?: string;
  };
  assigneeNames?: {
    preparer?: string;
    reviewer?: string;
    submitter?: string;
  };
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

export const TaskStatusModal: React.FC<TaskStatusModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  task
}) => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [assignees, setAssignees] = useState({
    preparerId: '',
    reviewerId: '',
    submitterId: '',
  });

  useEffect(() => {
    if (isOpen && task) {
      setSelectedStatus(task.status);
      setAssignees({
        preparerId: task.assignees.preparerId || '',
        reviewerId: task.assignees.reviewerId || '',
        submitterId: task.assignees.submitterId || '',
      });
      fetchUsers();
    }
  }, [isOpen, task]);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/firm-admin/users');
      if (response.data.success) {
        setUsers(response.data.data.users || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleStatusUpdate = async () => {
    if (!task || selectedStatus === task.status) return;

    setLoading(true);
    try {
      const response = await api.patch(`/firm-admin/tasks/${task.id}/status`, {
        status: selectedStatus
      });
      
      if (response.data.success) {
        toast.success('Task status updated successfully');
        onSuccess();
        onClose();
      }
    } catch (error: any) {
      console.error('Error updating task status:', error);
      toast.error(error.response?.data?.message || 'Failed to update task status');
    } finally {
      setLoading(false);
    }
  };

  const handleAssigneeUpdate = async () => {
    if (!task) return;

    const hasChanges = 
      assignees.preparerId !== (task.assignees.preparerId || '') ||
      assignees.reviewerId !== (task.assignees.reviewerId || '') ||
      assignees.submitterId !== (task.assignees.submitterId || '');

    if (!hasChanges) return;

    setLoading(true);
    try {
      const updateData = {
        assignees: {
          ...(assignees.preparerId && { preparerId: assignees.preparerId }),
          ...(assignees.reviewerId && { reviewerId: assignees.reviewerId }),
          ...(assignees.submitterId && { submitterId: assignees.submitterId }),
        }
      };

      const response = await api.patch(`/firm-admin/tasks/${task.id}`, updateData);
      
      if (response.data.success) {
        toast.success('Task assignees updated successfully');
        onSuccess();
        onClose();
      }
    } catch (error: any) {
      console.error('Error updating task assignees:', error);
      toast.error(error.response?.data?.message || 'Failed to update task assignees');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAll = async () => {
    if (!task) return;

    setLoading(true);
    try {
      // Update status if changed
      if (selectedStatus !== task.status) {
        await api.patch(`/firm-admin/tasks/${task.id}/status`, {
          status: selectedStatus
        });
      }

      // Update assignees if changed
      const hasAssigneeChanges = 
        assignees.preparerId !== (task.assignees.preparerId || '') ||
        assignees.reviewerId !== (task.assignees.reviewerId || '') ||
        assignees.submitterId !== (task.assignees.submitterId || '');

      if (hasAssigneeChanges) {
        const updateData = {
          assignees: {
            ...(assignees.preparerId && { preparerId: assignees.preparerId }),
            ...(assignees.reviewerId && { reviewerId: assignees.reviewerId }),
            ...(assignees.submitterId && { submitterId: assignees.submitterId }),
          }
        };

        await api.patch(`/firm-admin/tasks/${task.id}`, updateData);
      }

      toast.success('Task updated successfully');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error updating task:', error);
      toast.error(error.response?.data?.message || 'Failed to update task');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
      case 'submitted':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'in_progress':
        return <Play className="w-5 h-5 text-indigo-500" />;
      case 'in_review':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'ready_to_submit':
        return <Clock className="w-5 h-5 text-blue-500" />;
      case 'blocked':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'draft':
      default:
        return <FileText className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
      case 'submitted':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'in_progress':
        return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'in_review':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'ready_to_submit':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'blocked':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'draft':
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (!isOpen || !task) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Update Task Status & Assignment
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Task Info */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium text-gray-900 mb-2">{task.title}</h3>
            <div className="flex items-center gap-2">
              {getStatusIcon(task.status)}
              <span className={`px-2 py-1 text-xs font-medium rounded border ${getStatusColor(task.status)}`}>
                {task.status.replace('_', ' ').toUpperCase()}
              </span>
            </div>
          </div>

          {/* Status Update */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Update Status
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                { value: 'draft', label: 'DRAFT' },
                { value: 'in_progress', label: 'IN PROGRESS' },
                { value: 'in_review', label: 'IN REVIEW' },
                { value: 'ready_to_submit', label: 'READY TO SUBMIT' },
                { value: 'submitted', label: 'SUBMITTED' },
                { value: 'completed', label: 'COMPLETED' },
                { value: 'blocked', label: 'BLOCKED' }
              ].map((status) => (
                <button
                  key={status.value}
                  onClick={() => setSelectedStatus(status.value)}
                  className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-colors ${
                    selectedStatus === status.value
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {getStatusIcon(status.value)}
                  <span className="text-sm font-medium">
                    {status.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Assignee Update */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Update Assignees
            </label>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-600 mb-2">
                  Preparer
                </label>
                <select
                  value={assignees.preparerId}
                  onChange={(e) => setAssignees(prev => ({ ...prev, preparerId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Select preparer</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName}
                    </option>
                  ))}
                </select>
                {task.assigneeNames?.preparer && (
                  <p className="text-xs text-gray-500 mt-1">
                    Current: {task.assigneeNames.preparer}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-2">
                  Reviewer
                </label>
                <select
                  value={assignees.reviewerId}
                  onChange={(e) => setAssignees(prev => ({ ...prev, reviewerId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Select reviewer</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName}
                    </option>
                  ))}
                </select>
                {task.assigneeNames?.reviewer && (
                  <p className="text-xs text-gray-500 mt-1">
                    Current: {task.assigneeNames.reviewer}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-2">
                  Submitter
                </label>
                <select
                  value={assignees.submitterId}
                  onChange={(e) => setAssignees(prev => ({ ...prev, submitterId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Select submitter</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName}
                    </option>
                  ))}
                </select>
                {task.assigneeNames?.submitter && (
                  <p className="text-xs text-gray-500 mt-1">
                    Current: {task.assigneeNames.submitter}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              onClick={handleSaveAll}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update Task'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
